/**
 * The size of a basin we DRAW ourselves — the built-in "Undermount Basin".
 *
 * `assembleVanity` has always had an answer for this (`VANITY_DIMENSIONS.sink`,
 * 24" x 18" with an 8" bowl, shrunk to whatever cabinet it lands on) but it was
 * inline, so the model picker's Placeholder tile had no way to ask for it and
 * simply INHERITED the outgoing model's box instead. Swap a 14.3"-deep catalog
 * undermount — dev's deepest — to the placeholder and you got a 14"-deep bowl,
 * with no way to correct it: a sink is not in `DIMENSION_EDITABLE_TYPES`, so
 * the panel offers no size editor to undo it with.
 *
 * Extracted so the builder and the picker cannot answer differently.
 */

const { INCHES_TO_METERS } = require('./constants');

/**
 * The picker id for the built-in basin.
 *
 * It is the GENERIC placeholder id every type shares (`PROCEDURAL_TILE_ID` in
 * `proceduralTile.js`) — for a sink, that tile means "the procedural
 * undermount basin". Named here because both the routing predicate and the
 * cloud swap have to recognise it, and neither can import a web-app module.
 * `proceduralTile.test.js` pins the two equal so they cannot drift apart.
 */
const PROCEDURAL_BASIN_MODEL_ID = 'procedural';

/**
 * The basin we draw when no product was chosen.
 *
 * Sized off a real mid-range undermount — Kohler's Derring 19-1/2" oval
 * (`kohler-17889-rl`, 19.19" x 16.12" x 7.19") — rather than
 * `VANITY_DIMENSIONS.sink`, which is 24" x 18" x 8" and reads as a large basin
 * on a standard vanity. That constant stays where it is: it is also the
 * LAYOUT figure `generateCabinetColumns` and `createSinkFaucet` fall back to,
 * and this is only the bowl we render.
 */
/**
 * The mount of the basin we draw.
 *
 * Named rather than assumed, because the mount used to be resolved on a path
 * that never asked this module: with no catalog model, every model-derived
 * branch in `assembleVanity` was null and the chain fell through to the
 * STYLE's `fallbackSinkType` — `vessel` for modern. So the built-in
 * undermount basin was seated on top of the counter, and `isVesselSink` then
 * carried the error into the faucet mount. The geometry we render is an
 * undermount bowl; this is that fact, where the geometry is.
 */
const PROCEDURAL_BASIN_SINK_TYPE = 'undermount';

const PROCEDURAL_BASIN = {
  width: 19 * INCHES_TO_METERS,
  depth: 16 * INCHES_TO_METERS,
  height: 7 * INCHES_TO_METERS,
};

/** Cabinet face left either side of the bowl — what a door needs to read as one. */
const BASIN_SIDE_REVEAL = 3 * INCHES_TO_METERS;
/** Kept off the backsplash behind and the counter nosing in front. */
const BASIN_FRONT_BACK_MARGIN = 1.25 * INCHES_TO_METERS;

/**
 * The standard basin, shrunk to fit the vanity it sits in.
 *
 * Never grown: a small cabinet gets a smaller bowl, a large one still gets the
 * standard 24" x 18" rather than a bathtub-sized basin.
 *
 * @param {Object} vanity - the cabinet's own width/depth in metres
 * @returns {{width: number, depth: number, height: number}}
 */
function proceduralBasinDimensions({ width: vanityWidth, depth: vanityDepth }) {
  const standard = PROCEDURAL_BASIN;
  return {
    ...standard,
    width: Number.isFinite(vanityWidth)
      ? Math.min(standard.width, vanityWidth - 2 * BASIN_SIDE_REVEAL)
      : standard.width,
    depth: Number.isFinite(vanityDepth)
      ? Math.min(standard.depth, vanityDepth - 2 * BASIN_FRONT_BACK_MARGIN)
      : standard.depth,
  };
}

module.exports = {
  PROCEDURAL_BASIN,
  PROCEDURAL_BASIN_MODEL_ID,
  PROCEDURAL_BASIN_SINK_TYPE,
  BASIN_SIDE_REVEAL,
  BASIN_FRONT_BACK_MARGIN,
  proceduralBasinDimensions,
};
